/**
 * Server state for the workspace: one query for the whole document and one
 * mutation per operation, each carrying the version the screen read. Every
 * successful write returns the whole view, so the screen never reconstructs
 * what the server did to the text (the style pass in particular). A 409
 * stale means another tab or the relay saved first: the view is reloaded and
 * the person told; typed text is kept in the box until they decide. A 409
 * complete means the report finished elsewhere. A 403 or 422 carries the
 * reason in words. Everything else says what did not happen and offers Retry
 * (docs/ux/shell.md).
 */

import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiError, api, reasonOf, readActingAs } from '@/app/api';
import type { HeardWord, PlannedSuggestion, StyleRecord, SuggestionGroupInput, WorkspaceEventKind, WorkspaceView } from '@/app/types';
import { auditKey, detailKey } from '../matter/useMatter';

export const STALE_TEXT = 'The report changed in another tab or from dictation. Reloaded; your last change was not saved.';
export const COMPLETE_TEXT = 'The report was completed elsewhere. Reloaded; nothing here is edited any more.';

export function workspaceKey(ref: string) {
  return ['matter', ref, 'workspace'] as const;
}

const base = (ref: string) => `/api/matters/${encodeURIComponent(ref)}/workspace`;
export const exportUrl = (ref: string) => `${base(ref)}/export`;
export const previewUrl = (ref: string) => `${base(ref)}/preview`;

export function useWorkspace(ref: string) {
  return useQuery({
    queryKey: workspaceKey(ref),
    queryFn: () => api<WorkspaceView>(base(ref)),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: 5_000,
  });
}

function useReload(ref: string) {
  const client = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: workspaceKey(ref) }),
      client.invalidateQueries({ queryKey: detailKey(ref) }),
      client.invalidateQueries({ queryKey: auditKey(ref) }),
    ]);
  }, [client, ref]);
}

function failed(message: string, retry: () => void): void {
  toast.error(`${message} Nothing changed.`, { action: { label: 'Retry', onClick: retry } });
}

export type WriteVars = { path: string; method?: 'PUT' | 'POST'; json: Record<string, unknown>; failure: string; refused?: (reason: string) => void };

/**
 * The one mutation shape every workspace write uses: the server's whole view
 * replaces the cached one, and the 409 cases reload and say what happened.
 */
export function useWorkspaceWrite(ref: string): UseMutationResult<WorkspaceView, unknown, WriteVars> {
  const client = useQueryClient();
  const reload = useReload(ref);
  const mutation = useMutation({
    mutationFn: (vars: WriteVars) => api<WorkspaceView>(`${base(ref)}${vars.path}`, { method: vars.method ?? 'POST', json: vars.json }),
    onSuccess: (view) => {
      client.setQueryData<WorkspaceView>(workspaceKey(ref), view);
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void client.invalidateQueries({ queryKey: auditKey(ref) });
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        await reload();
        toast.error(body?.error === 'complete' ? COMPLETE_TEXT : STALE_TEXT);
        return;
      }
      const reason = reasonOf(err);
      if (reason && (err instanceof ApiError) && (err.status === 422 || err.status === 403 || err.status === 400)) {
        vars.refused?.(reason);
        toast.error(`${vars.failure} ${reason}.`);
        return;
      }
      failed(vars.failure, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

/** Convenience wrappers naming each operation; every one takes the version the screen holds. */
export function workspaceWrites(version: number) {
  return {
    saveText: (boxId: string, text: string, styled: boolean): WriteVars => ({ path: `/boxes/${encodeURIComponent(boxId)}`, method: 'PUT', json: { text, styled, version }, failure: 'Could not save the text.' }),
    undoStyle: (boxId: string, changeId: string): WriteVars => ({ path: `/boxes/${encodeURIComponent(boxId)}/undo`, json: { changeId, version }, failure: 'Could not undo the change.' }),
    confirmHeard: (boxId: string, id: string): WriteVars => ({ path: `/boxes/${encodeURIComponent(boxId)}/heard/${encodeURIComponent(id)}/confirm`, json: { version }, failure: 'Could not clear the mark.' }),
    proofread: (boxId: string, change: { start: number; end: number; before: string; after: string; rule: string }): WriteVars => ({ path: `/boxes/${encodeURIComponent(boxId)}/proofread`, json: { ...change, version }, failure: 'Could not apply the suggestion.' }),
    propose: (boxId: string, text: string, groups: SuggestionGroupInput[] | undefined): WriteVars => ({ path: `/boxes/${encodeURIComponent(boxId)}/propose`, json: { text, ...(groups ? { groups } : {}), version }, failure: 'Could not propose the edits.' }),
    acceptChange: (boxId: string): WriteVars => ({ path: `/boxes/${encodeURIComponent(boxId)}/accept-change`, json: { version }, failure: 'Could not accept the change.' }),
    decide: (id: string, decision: 'accept' | 'reject', reason?: string): WriteVars => ({ path: `/suggestions/${encodeURIComponent(id)}/decide`, json: { decision, ...(reason ? { reason } : {}), version }, failure: decision === 'accept' ? 'Could not accept the suggestion.' : 'Could not decline the suggestion.' }),
    revert: (id: string): WriteVars => ({ path: `/suggestions/${encodeURIComponent(id)}/revert`, json: { version }, failure: 'Could not revert the change.' }),
    decideAll: (sectionKey: string, decision: 'accept' | 'reject'): WriteVars => ({ path: `/sections/${encodeURIComponent(sectionKey)}/decide-all`, json: { decision, version }, failure: decision === 'accept' ? 'Could not accept the suggestions.' : 'Could not decline the suggestions.' }),
    ask: (boxId: string, question: string, aboutSuggestionId: string | null): WriteVars => ({ path: '/clarifications', json: { boxId, question, aboutSuggestionId, version }, failure: 'Could not send the question.' }),
    address: (id: string, answer: string): WriteVars => ({ path: `/clarifications/${encodeURIComponent(id)}/address`, json: { answer, version }, failure: 'Could not close the question.' }),
    event: (kind: WorkspaceEventKind, label: string): WriteVars => ({ path: `/events/${kind}`, json: { version }, failure: `Could not ${label.toLowerCase()}.` }),
  };
}

/* ------------------------------------------------------------ formatting */

export type FormatRange = { start: number; end: number } | null;

/** The Format button's answer (ADR-0002): a 200 carries the outcome and the view even when the validator refused the patch. */
export type FormatEnvelope =
  | { outcome: 'applied'; recordId: string; view: WorkspaceView }
  | { outcome: 'rejected'; code: string; reason: string; view: WorkspaceView }
  | { outcome: 'unchanged'; view: WorkspaceView };

export type FormatVars = { boxId: string; version: number; range: FormatRange };

/**
 * One formatting request: the range (or the whole box), the version the
 * screen holds, and the server's whole view back. 409 stale means the
 * document moved while the formatter was answering and nothing changed.
 */
export function useFormat(ref: string): UseMutationResult<FormatEnvelope, unknown, FormatVars> {
  const client = useQueryClient();
  const reload = useReload(ref);
  const mutation = useMutation({
    mutationFn: (vars: FormatVars) => api<FormatEnvelope>(`${base(ref)}/boxes/${encodeURIComponent(vars.boxId)}/format`, { method: 'POST', json: { version: vars.version, ...(vars.range ? { start: vars.range.start, end: vars.range.end } : {}) } }),
    onSuccess: (envelope) => {
      client.setQueryData<WorkspaceView>(workspaceKey(ref), envelope.view);
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void client.invalidateQueries({ queryKey: auditKey(ref) });
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        await reload();
        toast.error(body?.error === 'complete' ? COMPLETE_TEXT : 'The report changed while the formatter was working. Reloaded; nothing was formatted.');
        return;
      }
      const reason = reasonOf(err);
      if (reason && err instanceof ApiError && (err.status === 422 || err.status === 403 || err.status === 400)) {
        toast.error(`Could not format the text. ${reason}`);
        return;
      }
      failed('Could not format the text.', () => mutation.mutate(vars));
    },
  });
  return mutation;
}

/** "Add to dictionary" (D-0059): the word joins the doctor's key terms on the registry record; the view's dictionary follows. */
export async function addDictionaryWord(ref: string, word: string): Promise<{ dictionary: string[] }> {
  return api(`${base(ref)}/dictionary`, { method: 'POST', json: { word } });
}

/** QA's preview of a rewrite. Nothing is written; the result is what the doctor would see. */
export async function planEdits(ref: string, boxId: string, text: string, groups: SuggestionGroupInput[] | null): Promise<{ current: string; planned: PlannedSuggestion[] }> {
  return api(`${base(ref)}/boxes/${encodeURIComponent(boxId)}/plan`, { method: 'POST', json: { text, ...(groups ? { groups } : {}) } });
}

/* ------------------------------------------------------------ dictation */

export type Caret = { start: number; end: number } | null;

export type DictationState =
  | { phase: 'idle' }
  | { phase: 'connecting'; boxId: string }
  | { phase: 'listening'; boxId: string; mode: 'live' | 'synthetic'; interim: string; caret: Caret }
  | { phase: 'finishing'; boxId: string; mode: 'live' | 'synthetic'; interim: string; caret: Caret };

type FinalMessage = { type: 'final'; boxId: string; text: string; records: StyleRecord[]; heard: HeardWord[]; caret: number; version: number; promotedFromInterim: boolean };
/** The relay formatted a dictated chunk on its own (ADR-0002, automatic formatting on): the whole box text, its records, the caret after the patch. */
export type FormattedMessage = { type: 'formatted'; boxId: string; text: string; records: StyleRecord[]; heard: HeardWord[]; caret: number; version: number; range: { start: number; end: number }; signal: string };

export type DictationCallbacks = {
  /** A final landed: the box text and caret to show, and the version the document is now at. */
  onFinal: (m: FinalMessage) => void;
  /** A chunk was formatted behind the caret; the text and records replace the box, the caret follows the patch. */
  onFormatted?: (m: FormattedMessage) => void;
  onError: (message: string) => void;
  /** The session ended (done, error or the socket closing); the view is reloaded by the caller. */
  onDone: () => void;
};

/**
 * One dictation session at a time: a WebSocket to the relay, the microphone
 * as 250 ms opus chunks when the relay is live, the interim shown at the
 * caret and never saved, and the relay's finals handed back with the whole
 * box text. Stop asks the relay to flush; the session ends on `done`.
 */
export function useDictation(ref: string, callbacks: DictationCallbacks) {
  const [state, setState] = useState<DictationState>({ phase: 'idle' });
  const socket = useRef<WebSocket | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const cb = useRef(callbacks);
  cb.current = callbacks;

  const teardown = useCallback(() => {
    recorder.current?.stream.getTracks().forEach((t) => t.stop());
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    recorder.current = null;
    const s = socket.current;
    socket.current = null;
    if (s && s.readyState <= WebSocket.OPEN) s.close();
    setState({ phase: 'idle' });
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = useCallback((boxId: string, caret: Caret) => {
    teardown();
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${protocol}://${window.location.host}/ws/dictate`);
    socket.current = ws;
    setState({ phase: 'connecting', boxId });
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'start', matterRef: ref, boxId, caret, actingAs: readActingAs() }));
    };
    ws.onmessage = async (event) => {
      const m = JSON.parse(String(event.data)) as { type: string; [k: string]: unknown };
      if (m.type === 'ready') {
        const mode = m.mode as 'live' | 'synthetic';
        setState({ phase: 'listening', boxId, mode, interim: '', caret });
        if (mode === 'live') {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
            recorder.current = rec;
            rec.ondataavailable = (e) => { if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data); };
            rec.start(250);
          } catch {
            cb.current.onError('No microphone is available in this browser.');
            teardown();
            cb.current.onDone();
          }
        }
        return;
      }
      if (m.type === 'interim') {
        setState((s) => (s.phase === 'listening' || s.phase === 'finishing' ? { ...s, interim: String(m.text) } : s));
        return;
      }
      if (m.type === 'final') {
        const final = m as unknown as FinalMessage;
        setState((s) => (s.phase === 'listening' || s.phase === 'finishing' ? { ...s, interim: '', caret: { start: final.caret, end: final.caret } } : s));
        cb.current.onFinal(final);
        return;
      }
      if (m.type === 'formatted') {
        const formatted = m as unknown as FormattedMessage;
        setState((s) => (s.phase === 'listening' || s.phase === 'finishing' ? { ...s, caret: { start: formatted.caret, end: formatted.caret } } : s));
        cb.current.onFormatted?.(formatted);
        return;
      }
      if (m.type === 'error') {
        cb.current.onError(String(m.message ?? 'Dictation failed.'));
        teardown();
        cb.current.onDone();
        return;
      }
      if (m.type === 'done') {
        teardown();
        cb.current.onDone();
      }
    };
    ws.onerror = () => {
      cb.current.onError('Dictation could not connect.');
      teardown();
      cb.current.onDone();
    };
    ws.onclose = () => {
      if (socket.current === ws) { teardown(); cb.current.onDone(); }
    };
  }, [ref, teardown]);

  const stop = useCallback(() => {
    recorder.current?.stream.getTracks().forEach((t) => t.stop());
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    recorder.current = null;
    const s = socket.current;
    if (s && s.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify({ type: 'stop' }));
      setState((st) => (st.phase === 'listening' ? { ...st, phase: 'finishing' } : st));
    } else {
      teardown();
      cb.current.onDone();
    }
  }, [teardown]);

  return { state, start, stop };
}
